import { useCallback, useEffect, useState } from 'react'

type UseHorizontalScrollOptions = {
  scrollAmount?: number
}

type UseHorizontalScrollReturn = {
  canScrollLeft: boolean
  canScrollRight: boolean
  scrollLeft: () => void
  scrollRight: () => void
}

/**
 * A custom React hook that provides horizontal scrolling functionality.
 *
 * @param scrollRef - A ref to the scrollable element
 * @param options - Configuration options for the hook
 * @param options.scrollAmount - Amount to scroll by (default: 200)
 * @returns An object containing:
 *   - canScrollLeft: boolean - Whether the element can be scrolled to the left
 *   - canScrollRight: boolean - Whether the element can be scrolled to the right
 *   - scrollLeft: () => void - Function to scroll the element to the left by scrollAmount
 *   - scrollRight: () => void - Function to scroll the element to the right by scrollAmount
 */
export const useHorizontalScroll = (
  scrollRef: React.RefObject<HTMLElement>,
  { scrollAmount = 200 }: UseHorizontalScrollOptions = {},
): UseHorizontalScrollReturn => {
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const checkScrollPosition = useCallback((): void => {
    const element = scrollRef.current
    if (!element) return

    const { scrollLeft, scrollWidth, clientWidth } = element
    setCanScrollLeft(scrollLeft > 0)
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1)
  }, [scrollRef])

  const scrollLeft = useCallback((): void => {
    const element = scrollRef.current
    if (!element) return

    element.scrollBy({ left: -scrollAmount, behavior: 'smooth' })
  }, [scrollRef, scrollAmount])

  const scrollRight = useCallback((): void => {
    const element = scrollRef.current
    if (!element) return

    element.scrollBy({ left: scrollAmount, behavior: 'smooth' })
  }, [scrollRef, scrollAmount])

  useEffect(() => {
    checkScrollPosition()
    const element = scrollRef.current

    if (element) {
      element.addEventListener('scroll', checkScrollPosition)
      window.addEventListener('resize', checkScrollPosition)
      return (): void => {
        element.removeEventListener('scroll', checkScrollPosition)
        window.removeEventListener('resize', checkScrollPosition)
      }
    }
  }, [checkScrollPosition, scrollRef])

  return {
    canScrollLeft,
    canScrollRight,
    scrollLeft,
    scrollRight,
  }
}
